import { CssBaseline } from '@material-ui/core';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import { lazy, Suspense, useContext, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { RouteProps, useHistory } from 'react-router-dom';
import { IntercomProvider } from 'react-use-intercom';

import { getBaseUrl } from '@core/base-url';
import { ConfigContext, useLandlordConstants } from '@core/contexts/ConfigContext';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import ROUTES from '@landlord/routes/routes.constant';
import { browserEventActions } from '@redux/reducers/browserEvent/browserEventReducer';
import { Confirmation } from '@shared/components/confirmation';
import { ErrorHandler } from '@shared/components/error-handler';
import { Layout } from '@shared/components/layout';
import { Notification } from '@shared/components/notification';
import { SessionChangedOverlay } from '@shared/components/session-changed-overlay';
import { sendGaEvent, useGoogleAnalytics } from '@shared/hooks/googleAnalytics';
import { useActingUserGuard } from '@shared/hooks/useActingUserGuard';
import { useHotjar } from '@shared/hooks/useHotJar';
import { isJamParam, useJam } from '@shared/hooks/useJam';
import { carrySearchParams } from '@shared/utils/route';

import { styles } from './App.styles';

const Disposals = lazy(() => import('@landlord/modules/disposals'));
const Agents = lazy(() => import('@landlord/modules/agents'));
const Dashboard = lazy(() => import('@landlord/modules/dashboard'));
const IasEvents = lazy(() => import('@landlord/modules/ias-events'));
const Present = lazy(() => import('@landlord/modules/present'));
const Viewings = lazy(() => import('@landlord/modules/viewings'));
const Integrations = lazy(() => import('@landlord/modules/integrations'));
const ScheduleInvites = lazy(() => import('@landlord/modules/schedule-invites'));
const Settings = lazy(() => import('@landlord/modules/settings'));
const StyleGuide = lazy(() => import('@landlord/modules/style-guide'));

export type AppProps = WithStyles<typeof styles>;

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler
);

const AppComponent: React.FC<AppProps> = ({ classes }) => {
  const { constants } = useLandlordConstants();
  const { config, error } = useContext(ConfigContext);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  useGoogleAnalytics(() => [
    {
      gtagOptions: { send_page_view: false, user_id: config.user.id },
      trackingId: config.googleAnalyticsTrackingId,
    },
  ]);

  useHotjar({ hotjarId: constants.HOTJAR_ID });

  useJam({ teamId: constants.JAM_ID });

  const actingUserChanged = useActingUserGuard();

  const transitionExclusionRoutes = [
    {
      routes: [ROUTES.organisation.disposals.root, ROUTES.organisation.disposals.manage.reporting],
    },
  ];

  const canDisableTransition = useMemo(() => {
    return transitionExclusionRoutes.some(({ routes }) => {
      return routes.every((route) => location.pathname.includes(route));
    });
  }, [location.pathname]);

  // Listen for changes on history
  useEffect(() => {
    let sendGaTimeout: number | undefined;

    const unregister = history.listen((location) => {
      if (history.action === 'POP') {
        dispatch(browserEventActions.backForwardBrowserEvent(location.search));
      } else {
        dispatch(browserEventActions.urlBrowserEvent(location.search));
      }

      // Submit page views
      clearTimeout(sendGaTimeout);
      sendGaTimeout = setTimeout(() => sendGaEvent(location.pathname, location.search), 100);
    });

    return () => {
      clearTimeout(sendGaTimeout);
      unregister();
    };
  }, [history, dispatch]);

  const routes = useMemo(() => {
    const iasAccess = config.featuresEnabled.landlordApp.ias_access;

    const module: Array<{
      access: boolean;
      routeProps: MergeTypes<{ component: React.ComponentType<any> }, Omit<RouteProps, 'component'>>;
    }> = [
      {
        access: true,
        routeProps: { path: ROUTES.organisation.dashboard, component: Dashboard },
      },
      {
        access: iasAccess,
        routeProps: { path: ROUTES.organisation.agents.root, component: Agents },
      },
      {
        access: iasAccess,
        routeProps: { path: ROUTES.organisation.ias_events, component: IasEvents },
      },
      {
        access: true,
        routeProps: { path: ROUTES.organisation.disposals.root, component: Disposals },
      },
      {
        // access: config.featuresEnabled.landlordApp.tasks , //Uncomment after aligning featuresEnabled.landlordApp.tasks with app nav
        access: true,
        routeProps: { path: ROUTES.organisation.viewings.root, component: Viewings },
      },
      {
        access: true,
        routeProps: { path: ROUTES.organisation.integrations.root, component: Integrations },
      },
      {
        access: config.appNav.schedule_invites.visible,
        routeProps: { path: ROUTES.organisation.scheduleInvites.root, component: ScheduleInvites },
      },
      {
        access: config.appNav.settings_style_guide.visible,
        routeProps: { path: ROUTES.styleGuide.root, component: StyleGuide },
      },
      // Presentation routes
      {
        access: true,
        routeProps: { path: ROUTES.present.root, component: Present },
      },
      {
        access: true,
        routeProps: {
          path: ROUTES.logOut,
          component: () => {
            window.location.href = `${getBaseUrl()}logout`;
            return null;
          },
        },
      },
      // --- Settings ---
      {
        access: config.appNav.settings_profile.visible,
        routeProps: { path: ROUTES.organisation.settings.root, component: Settings },
      },
    ];

    return module.filter(({ access }) => access).map(({ access, ...otherProps }) => otherProps);
  }, [config.featuresEnabled.landlordApp.ias_access, config.featuresEnabled.landlordApp.tasks]);

  if (error) {
    return (
      <ErrorHandler
        heading={error || `Sorry, we're having a few technical troubles right now.`}
        classes={{ root: classes.errorHandler }}
      />
    );
  }

  return (
    <IntercomProvider
      appId={config.intercomId}
      autoBoot={config.intercomEnabled}
      autoBootProps={{
        // Keep consistent with agency AngularJS app + agency React app
        email: config.user.email,
        name: config.user.name,
        userHash: config.intercom_hash,
        userId: config.user.id.toString(),
      }}
    >
      {actingUserChanged && <SessionChangedOverlay />}
      <CssBaseline />
      <Confirmation />
      <Notification />
      <Layout googleMapsApiKey={constants.GOOGLE_MAPS_HTTP_API} disableTransition={canDisableTransition}>
        <Suspense fallback={null}>
          <Switch>
            {/* Routes */}
            {routes.map(({ routeProps: { component: $component, ...otherRouteProps } }) => (
              <Route key={String(otherRouteProps.path)} component={$component} {...otherRouteProps} />
            ))}
            <Redirect exact from={ROUTES.initial} to={carrySearchParams(ROUTES.organisation.dashboard, isJamParam)} />
            <Redirect to={ROUTES.defaultRedirect} />
          </Switch>
        </Suspense>
      </Layout>
    </IntercomProvider>
  );
};

export const App = withStyles(styles)(AppComponent);
